import { ComponentProps } from 'react';
import { cn } from '@/lib/utils';
import React from 'react';
import { Check, Copy } from 'lucide-react';

export const CodeBlock = ({ className, children, ...props }: ComponentProps<'code'>) => {
    const language = className?.replace(/language-|hljs\s?/g, '').trim() || 'text';
    const isInline = !className?.includes('language-');
    const [isCopied, setIsCopied] = React.useState(false);
    const codeRef = React.useRef<HTMLElement>(null);

    const handleCopy = async () => {
        if (!codeRef.current) return;
        const text = codeRef.current.textContent || '';
        try {
            await navigator.clipboard.writeText(text);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    if (isInline) {
        return (
            <code className={cn(
                'bg-muted px-1.5 py-0.5 rounded text-sm font-mono',
                className
            )}
                {...props}
            >
                {children}
            </code>
        );
    }

    return (
        <div className="my-4 rounded-lg overflow-hidden group">
            <div className="bg-muted/50 px-4 py-2 text-sm font-mono border-b border-border flex justify-between items-center">
                <span>{language}</span>
                <button
                    onClick={handleCopy}
                    className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded p-1"
                    aria-label={isCopied ? "Copied to clipboard" : "Copy code"}
                >
                    {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
            </div>
            <pre
                className="bg-muted p-4 overflow-x-auto"
                tabIndex={0}
                role="region"
                aria-label={`${language} code snippet`}
            >
                <code ref={codeRef} className={cn('font-mono', className)} {...props}>
                    {children}
                </code>
            </pre>
        </div>
    );
};
